import { UpdateSettingsMutationVariables } from '~/data';
import { SliceCreator } from '../TrainingState';
import { persistSettings } from './persistSettings';
import { SettingsSlice as Slice } from './SettingsSlice';

export const createSettingsSlice: SliceCreator<Slice> = (set, get) => {
  /**
   * Saves all settings to the back-end and updates the local store
   * @param newValues One or more new setting value
   */
  async function updateSettings(
    newValues: Partial<UpdateSettingsMutationVariables>,
    isOnboarding?: boolean,
    setLocale?: boolean
  ) {
    const {
      learnerTrainingId,
      textLocale,
      mediumLocale,
      subtitleLocale,
      subtitles,
      auditoryFeedback,
      visualFeedback,
      darkMode,
      autoPlay,
      playbackSpeed
    } = get();

    if (!learnerTrainingId) return;

    const dataForSending = {
      learnerTrainingUuid: learnerTrainingId,
      textLocale,
      mediumLocale,
      subtitleLocale,
      subtitles,
      auditoryFeedback,
      visualFeedback,
      darkMode,
      autoPlay,
      playbackSpeed,
      ...newValues
    } as UpdateSettingsMutationVariables;

    const updatedSettings = await persistSettings(
      isOnboarding
        ? ({
            learnerTrainingUuid: learnerTrainingId,
            locale: newValues.locale || dataForSending.textLocale,
            subtitles: dataForSending.subtitles,
            auditoryFeedback: dataForSending.auditoryFeedback,
            visualFeedback: dataForSending.visualFeedback,
            darkMode: dataForSending.darkMode,
            autoPlay: dataForSending.autoPlay,
            playbackSpeed: dataForSending.playbackSpeed
          } as UpdateSettingsMutationVariables)
        : setLocale
        ? ({
            learnerTrainingUuid: learnerTrainingId,
            locale: newValues.locale || dataForSending.textLocale
          } as UpdateSettingsMutationVariables)
        : dataForSending
    );

    set(updatedSettings);
  }

  return {
    textLocale: '',
    textLocales: [],
    subtitleLocale: '',
    subtitleLocales: [],
    languageAutoDetect: true,
    languages: [],
    mediumLocale: '',
    mediumLocales: [],
    subtitles: false,
    playbackSpeed: 1,
    askName: false,
    autoPlay: false,
    fasterPlaybackSpeed: true,
    auditoryFeedback: true,
    visualFeedback: true,
    supportedLanguages: {},
    darkMode: 'automatic',

    setLocale: locale => {
      updateSettings({ locale }, false, true);
    },

    setTextLocale: (textLocale, changeSubtitles, isOnboarding) => {
      if (textLocale === get().textLocale) return;

      if (changeSubtitles && textLocale !== 'en') {
        updateSettings({ textLocale, subtitles: true }, isOnboarding);
      } else {
        updateSettings({ textLocale }, isOnboarding);
      }
    },

    setMediumLocale: mediumLocale => {
      if (mediumLocale === get().mediumLocale) return;
      updateSettings({ mediumLocale });
    },

    setSubtitles: subtitles => {
      if (subtitles === get().subtitles) return;
      updateSettings({ subtitles });
    },

    setPlaybackSpeed: playbackSpeed => {
      if (playbackSpeed === get().playbackSpeed) return;
      updateSettings({ playbackSpeed });
    },

    setAuditoryFeedback: auditoryFeedback => {
      updateSettings({ auditoryFeedback });
    },

    setVisualFeedback: visualFeedback => {
      updateSettings({ visualFeedback });
    },

    setDarkMode: darkMode => {
      updateSettings({ darkMode });
    },

    setAutoPlay: autoPlay => {
      if (autoPlay === get().autoPlay) return;
      updateSettings({ autoPlay });
    },

    setSubtitleLocale: subtitleLocale => {
      if (subtitleLocale === get().subtitleLocale) return;
      updateSettings({ subtitleLocale });
    }
  };
};
